import { Box, Fab, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import { AppHeader } from "JS/React/Components/AppHeader";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import qs from "qs";
import {
    useGlobalPlaylists,
    usePlaylistAudios,
    usePlaylists,
    useRemoveAudio,
    useSortAudios,
} from "JS/React/Hooks/Playlist";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppTypography } from "JS/React/Components/AppTypography";
import {
    getDownloadedPartsNids,
    isBundleInProgress,
    isPlaylistDownloaded,
} from "JS/Helpers";
import { fetchBatchThumbnailUrls } from "JS/React/Hooks/Common/ImageUpload";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberAudioGifts,
    useReceivedProspectAudioGifts,
} from "JS/React/Hooks/Gifts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSnackbar } from "JS/Helpers/Snackbar";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    Content,
    ContentWithMedia,
    EventActions,
    EventCategories,
    EventNames,
    GiftWithMedia,
    ReceivedGift,
} from "JS/Models";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { getDexieConnectionRefresh } from "JS/Database/Dexie";
import { store, useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { setDownloadedContentNids } from "JS/Redux/download";
import { PlaylistElement } from "./Components/PlaylistElement";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { useDeleteAudios, useDownloadAudios } from "JS/React/Hooks/Database";
import { isInDownloadQueue } from "JS/Helpers/ContentDownloadHelper";
import { config } from "JS/Config";
import { DownloadWarningDialog } from "JS/React/Components/DownloadWarningDialog";
import { AppDivider } from "JS/React/Components/AppDivider";
import { AppDownloadingIcons } from "JS/React/Components/Icon/AppDownloadingIcons";
import { useGestureToScroll } from "JS/React/Hooks/Common/GestureControl";

export const PlaylistDetail = (props: PlaylistDetailProps) => {
    const { className } = props;
    const { userPlaylists: playlists, playlistResumeState } =
        useGlobalPlaylists();
    const params: any = useParams();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { id } = params;
    const { loading: playlistLoading, error, getAudios } = usePlaylistAudios();
    const {
        audios: allAudios,
        resumeUserAudios,
        resumeUserBundles,
        details,
        getAudioDetails,
    } = useGlobalAudios();
    const [isEdit, setEdit] = useState(false);

    // download playlist
    const [isContentDownloaded, setIsContentDownloaded] =
        useState<boolean>(false);
    const [isPartialDownloaded, setIsPartialDownloaded] =
        useState<boolean>(false);
    const [isCheckingDownload, setIsCheckingDownload] = useState(true);
    const [isOpenConfirmDailog, setIsOpenConfirmDailog] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const inProgressNid = useAppSelector(
        (state) => state.download.inProgressNid,
    );

    const dispatch = useAppDispatch();

    const { enqueueSnackbar } = useSnackbar();
    const { loading: audioLoading } = useAudios();
    const { isProsUser, loading: loadingProsStatus } = useProspectingUser();
    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();
    const { loading: rmgLoading } = useReceivedMemberAudioGifts(
        !!receivedMemberAudios?.length,
    );
    const { loading: rmpLoading } = useReceivedProspectAudioGifts(
        !isProsUser || !!receivedProspectAudios?.length,
    );
    const { loading: playlistsLoading } = usePlaylists(!!playlists?.length);
    const { favNids } = useGlobalFavorites();

    const { deleteAudiosById } = useDeleteAudios();
    useGetFavorites(!!favNids?.length);

    const { logFirebaseEvent } = useFirebaseLogger();

    const [downloadConfirmDialog, setDownloadConfirmDialog] = useState(false);

    const {
        loading: addToPlaylistLoading,
        downloadAudios,
        limitReached,
        setLimitReached,
    } = useDownloadAudios();

    const playlist = useMemo(() => {
        return playlists?.find((p) => p.id === +id);
    }, [playlists]);

    useEffect(() => {
        if (playlist)
            logFirebaseEvent(EventNames.AUDIO_PLAYLIST_OPENED, {
                action: EventActions.OPEN,
                category: EventCategories.AUDIO_PLAYLIST,
                playlistId: playlist.id.toString(),
            });
    }, [playlist?.id]);

    const [audiosInPlaylist, setAudiosInPlaylist] =
        useState<(ContentWithMedia | GiftWithMedia)[]>();

    useEffect(() => {
        if (playlist?.audios)
            setAudiosInPlaylist(
                [...playlist?.audios]
                    ?.sort((a, b) => +a.sortOrder - +b.sortOrder)
                    ?.map((a) => {
                        const selfAudio = allAudios.find(
                            (aa) => aa.nid === a.nid,
                        );
                        if (selfAudio)
                            return { ...selfAudio, ...getAudioDetails(a.nid) };
                        const memberGiftAudio = receivedMemberAudios.find(
                            (rmg) => rmg.nid === a.nid,
                        );
                        return memberGiftAudio
                            ? {
                                  ...memberGiftAudio,
                                  ...getAudioDetails(a.nid),
                              }
                            : {
                                  ...receivedProspectAudios.find(
                                      (rgp) => rgp.nid === a.nid,
                                  ),
                                  ...getAudioDetails(a.nid),
                              };
                    })
                    .filter((a) => a !== undefined),
            );
    }, [
        playlist,
        allAudios,
        receivedMemberAudios,
        receivedProspectAudios,
        details,
    ]);

    const [thumbnails, setThumbnails] = useState<{
        [key: string]: string;
    }>({});
    const fetchedNidsRef = useRef<string>("");

    useEffect(() => {
        if (audiosInPlaylist?.length) {
            const nidsKey = audiosInPlaylist.map((a) => a.nid).join(",");
            if (nidsKey !== fetchedNidsRef.current) {
                fetchedNidsRef.current = nidsKey;
                fetchThumbnails();
            }
        }
    }, [audiosInPlaylist]);
    const { audioList, setAudioList, listingLoading, updateAudioStatus } =
        useListing(audiosInPlaylist, inProgressNid, isEdit);

    const { onConfirmRemoveAudioFromPlaylist, removeLoading } = useHandlers();

    useEffect(() => {
        if (!!audiosInPlaylist?.length) {
            isPlaylistDownloaded(audiosInPlaylist)
                .then((res) => {
                    setIsCheckingDownload(false);
                    setIsContentDownloaded(res.isCompleteDownloaded);
                    setIsPartialDownloaded(res.isPartiallyDownloded);
                })
                .catch((err) => {
                    setIsCheckingDownload(false);
                    setIsContentDownloaded(false);
                    setIsPartialDownloaded(false);
                });
        }
    }, [audiosInPlaylist?.length, inProgressNid]);

    const loading =
        playlistLoading ||
        audioLoading ||
        rmgLoading ||
        rmpLoading ||
        loadingProsStatus ||
        listingLoading ||
        playlistsLoading;

    const fetchThumbnails = async () => {
        const items = audiosInPlaylist.map((a) => ({
            nid: a.nid,
            postfix: a.image_url_postfix,
            fileName: a.image_file_name,
        }));

        const newThumbnails = await fetchBatchThumbnailUrls(items);
        setThumbnails(newThumbnails);
    };

    const itemRefs = useRef([]);
    const { scrollTo } = useQueryParams();

    useEffect(() => {
        if (
            !loading &&
            !error &&
            audioList?.length &&
            +scrollTo >= 0 &&
            itemRefs?.current[+scrollTo]
        ) {
            itemRefs.current[+scrollTo]?.scrollIntoView({
                behavior: "instant",
            });
            window.scrollTo(0, 500);
            window.scrollTo(0, 0);
        }
    }, [scrollTo, loading, error, audioList?.length]);

    const isFavouriteContent = useCallback(
        (filteredAudio: ReceivedGift | Content) =>
            favNids.includes(filteredAudio.nid),
        [favNids],
    );

    useEffect(() => {
        getAudios(id);
    }, [id]);

    const [audioForRemoval, setAudioForRemoval] = useState<
        Content | ReceivedGift
    >();

    const { loading: sortLoading, sortAudios } = useSortAudios();

    const onSortEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = audioList.findIndex((a) => a.audio.nid === active.id);
            const newIndex = audioList.findIndex((a) => a.audio.nid === over.id);
            setAudiosInPlaylist(arrayMove(audiosInPlaylist, oldIndex, newIndex));
            setAudioList(arrayMove(audioList, oldIndex, newIndex));
        }
    };

    const isResumeable = useCallback(
        (value: Content) => {
            if (+value?.no_of_files > 1) {
                let filterResumeBundle = resumeUserBundles?.filter(
                    (x) => x.nid === value.nid,
                );
                return filterResumeBundle?.length > 0;
            } else {
                if (
                    !!resumeUserAudios?.filter((x) => x.nid === value.nid)
                        .length
                ) {
                    const resumeData = resumeUserAudios.find(
                        (x) => x.nid === value.nid,
                    );
                    return !!resumeData;
                } else {
                    return false;
                }
            }
        },
        [resumeUserAudios, resumeUserBundles],
    );

    const showDeletionSnackbar = () => {
        enqueueSnackbar(messaging.download.deleteSuccess, {
            variant: "success",
        });

        setDeleteLoader(false);
        setIsContentDownloaded(false);
        setIsPartialDownloaded(false);
        updateAudioStatus();
    };

    const handleToggleConfirmDialog = () => {
        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const handleDelete = async () => {
        const onDeleteSuccess = (deletedNids: string[]) => {
            const nids = store.getState().download.downloadedContentNids;

            const downloadedNids = nids && nids.length ? [...nids] : [];

            const deletedIds = deletedNids.map(
                (d) => `${config.user.memberId}-${d}`,
            );
            const newNids = downloadedNids.filter(
                (d) => !deletedIds.includes(d),
            );

            showDeletionSnackbar();
            dispatch(
                setDownloadedContentNids({
                    downloadedContentNids: newNids,
                }),
            );
        };

        setDeleteLoader(true);

        let toDeleteIds: string[] = [];

        for (let j = 0; j < audiosInPlaylist.length; j++) {
            const { nid } = audiosInPlaylist[j];

            if (+audiosInPlaylist[j].no_of_files > 1) {
                await getDownloadedPartsNids(nid).then(async (res) => {
                    if (res.length) {
                        toDeleteIds = [...toDeleteIds, ...res];
                    }
                });
            } else {
                toDeleteIds = [
                    ...toDeleteIds,
                    `${config.user.memberId}-${nid}`,
                ];
            }
        }

        await deleteAudiosById(toDeleteIds, () => {
            onDeleteSuccess(toDeleteIds);
        });
        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const handleDownload = async () => {
        toggleDownloadDialog();
        downloadAudios(audiosInPlaylist);
    };

    const toggleDownloadDialog = () => {
        setDownloadConfirmDialog(!downloadConfirmDialog);
    };

    const toggleQueueLimitReached = () => {
        setLimitReached(undefined);
    };

    const playClick = () => {
        if (!!audioList.length && !!playlist?.audios?.length) {
            logFirebaseEvent(EventNames.AUDIO_PLAYLIST_PLAYED, {
                category: EventCategories.AUDIO_PLAYLIST,
                action: EventActions.PLAY,
                playlistId: id,
            });
            navigate(
                linkProvider.react
                    .playlist()
                    .play(
                        id,
                        playlistResumeState(
                            playlist,
                        )?.resumeAudioIndex?.toString(),
                        playlistResumeState(
                            playlist,
                        )?.resumeBundleIndex?.toString(),
                    ),
            );
        } else showEmptyPlaylistSnackbar();
    };

    const showEmptyPlaylistSnackbar = () =>
        enqueueSnackbar(messaging.playlist.emptyError, {
            variant: "warning",
        });

    return (
        <Layout>
            <HeaderWrapper>
                <AppHeader
                    title={playlist?.name}
                    canGoBack
                    searchable={false}
                    onBackClick={() => {
                        navigate(linkProvider.react.playlist().index());
                    }}
                />
                <FabWrapper className={className}>
                    <StyledFab
                        color="primary"
                        aria-label="add"
                        onClick={playClick}
                    >
                        <FontAwesomeIcon icon={["fas", "play"]} />
                    </StyledFab>
                    <Box height={"5px"} />
                    <Box height={"5px"} />
                    <StyledFab
                        color="primary"
                        aria-label="edit"
                        onClick={() => {
                            if (audioList?.length && playlist?.audios?.length) {
                                if (isEdit)
                                    sortAudios(
                                        id,
                                        audioList.map((a) => a.audio),
                                    );
                                setEdit(!isEdit);
                            } else showEmptyPlaylistSnackbar();
                        }}
                    >
                        {isEdit ? (
                            <FontAwesomeIcon icon={["fas", "circle-check"]} />
                        ) : (
                            <FontAwesomeIcon icon={["fas", "pen-to-square"]} />
                        )}
                    </StyledFab>
                    <Box height={"5px"} />
                    <Box height={"5px"} />
                    {audioList?.length && playlist?.audios?.length ? (
                        <>
                            {!isContentDownloaded && !isCheckingDownload && (
                                <>
                                    <DownloadWarningDialog
                                        onConfirm={toggleDownloadDialog}
                                    >
                                        {(renderProps) => (
                                            <StyledFab
                                                color="primary"
                                                role="button"
                                                aria-label="download"
                                                onClick={
                                                    renderProps.askConfirmation
                                                }
                                            >
                                                <AppDownloadingIcons
                                                    iconType="download"
                                                    isFloating
                                                />
                                            </StyledFab>
                                        )}
                                    </DownloadWarningDialog>

                                    <Box height={"5px"} />
                                    <Box height={"5px"} />
                                </>
                            )}
                            {isCheckingDownload && (
                                <>
                                    <StyledFab color="primary">
                                        <WhiteCircularProgress
                                            loaderSize="small"
                                        />
                                    </StyledFab>
                                    <Box height={"5px"} />
                                    <Box height={"5px"} />
                                    <StyledFab color="primary">
                                        <WhiteCircularProgress
                                            loaderSize="small"
                                        />
                                    </StyledFab>
                                </>
                            )}

                            {(isPartialDownloaded || isContentDownloaded) &&
                                !isCheckingDownload && (
                                    <StyledFab
                                        color="primary"
                                        role="button"
                                        aria-label="delete"
                                        onClick={handleToggleConfirmDialog}
                                    >
                                        <AppDownloadingIcons
                                            isFloating
                                            iconType="trash"
                                        />
                                    </StyledFab>
                                )}
                        </>
                    ) : (
                        ""
                    )}
                </FabWrapper>
            </HeaderWrapper>

            <FixedListing>
                <ContentWrapper>
                    {!loading &&
                        !error &&
                        !!audioList?.length &&
                        !!playlist?.audios?.length && (
                            <SortableAudios
                                items={audioList}
                                isFavouriteContent={isFavouriteContent}
                                isEdit={isEdit}
                                isResumeable={isResumeable}
                                thumbnails={thumbnails}
                                id={id}
                                setAudioForRemoval={setAudioForRemoval}
                                onSortEnd={onSortEnd}
                                itemRefs={itemRefs}
                            />
                        )}

                    {!!error && (
                        <ContentTypography align="center" variant="h6">
                            {error}
                        </ContentTypography>
                    )}

                    {!loading && !error && playlist?.audios?.length === 0 && (
                        <ContentTypography align="center" variant="h6">
                            {messaging.playlist.emptyContent}
                        </ContentTypography>
                    )}
                </ContentWrapper>
            </FixedListing>

            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.fetch_playlist}
                />
            )}
            {removeLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={"Removing audio"}
                />
            )}
            {sortLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={"Updating playlist items position"}
                />
            )}

            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.fetch_playlist}
                />
            )}
            {addToPlaylistLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.addingFilesToQueue}
                />
            )}
            {removeLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={"Removing audio"}
                />
            )}
            {sortLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={"Updating playlist items position"}
                />
            )}

            {audioForRemoval && !removeLoading && (
                <AppConfimationDialog
                    open
                    onConfirm={() => {
                        onConfirmRemoveAudioFromPlaylist(
                            id,
                            audioForRemoval,
                            audioForRemoval.isReceived,
                        );
                        setAudioForRemoval(undefined);
                        setEdit(false);
                    }}
                    onClose={() => setAudioForRemoval(undefined)}
                    popupMainText="Are you sure you want to remove this audio?"
                    confirmText="Yes"
                    cancelButtonText="No"
                />
            )}
            {isOpenConfirmDailog && (
                <AppConfimationDialog
                    open={isOpenConfirmDailog}
                    onConfirm={handleDelete}
                    btnConfirmLoader={deleteLoader}
                    onClose={handleToggleConfirmDialog}
                    popupMainText={`Are you sure you want to delete downloaded playlist?`}
                    confirmText="Delete"
                    cancelButtonText="Cancel"
                    showConfirmFirst={true}
                />
            )}
            {limitReached && (
                <AppConfimationDialog
                    titleText={messaging?.downloadingQueue?.QueueLimitation}
                    showTitle={true}
                    open={!!limitReached}
                    popupMainText={
                        limitReached?.count === 0
                            ? messaging?.downloadingQueue?.limitReached
                            : `There are ${limitReached?.count} audios to download. 50 audios can download at one time. Click Download All again to finish downloading all audios.`
                    }
                    cancelButtonText="OK"
                    onClose={toggleQueueLimitReached}
                />
            )}

            {audioList?.length && downloadConfirmDialog && (
                <AppConfimationDialog
                    open
                    onConfirm={handleDownload}
                    onClose={toggleDownloadDialog}
                    popupMainText="Are you sure you want to download all content in this playlist?"
                    confirmText="Yes"
                    cancelButtonText="No"
                />
            )}
        </Layout>
    );
};

interface PlaylistAudio {
    audio: Content | ReceivedGift;
    state: AudioDownloadState;
}

enum AudioDownloadState {
    DOWNLOADING,
    QUEUED,
    DOWNLOADED,
    NONE,
}

const useListing = (
    audiosInPlaylist: (Content | GiftWithMedia)[],
    inProgressNid: string,
    isEdit: boolean,
) => {
    const [listingLoading, setListingLoading] = useState(false);
    const [audioList, setAudioList] = useState<PlaylistAudio[]>();
    const isInitialRun = useRef(true);
    const progressIdRef = useRef(inProgressNid);
    const audiosRef = useRef(audiosInPlaylist);

    const isDownloading = (nid: string, length: number) => {
        if (length > 1) return isBundleInProgress(nid);
        else return progressIdRef.current.includes(nid);
    };

    const isAudioDownloaded = async (audioNid: string, length: number) => {
        if (length > 1) {
            const downloadedPartsNids = await getDownloadedPartsNids(audioNid);
            return downloadedPartsNids?.length === length;
        } else {
            const response = await getDexieConnectionRefresh().audios.get(
                `${config.user.memberId}-${audioNid}`,
            );

            return response ? true : false;
        }
    };

    const updateAudioStatus = () => {
        if (!!audiosRef?.current?.length) {
            setListingLoading(isInitialRun.current);
            if (isInitialRun.current) isInitialRun.current = false;
            Promise.all(
                audiosRef.current?.map(async (a) => {
                    let state = AudioDownloadState.NONE;
                    const downloading = isDownloading(a.nid, +a?.no_of_files);
                    if (downloading) state = AudioDownloadState.DOWNLOADING;
                    else if (isInDownloadQueue(a.nid))
                        state = AudioDownloadState.QUEUED;
                    else {
                        const isDownloaded = await isAudioDownloaded(
                            a.nid,
                            +a?.no_of_files,
                        );
                        if (isDownloaded) state = AudioDownloadState.DOWNLOADED;
                    }
                    return { audio: a, state } as PlaylistAudio;
                }),
            )
                .then((val) => {
                    setAudioList(val);
                })
                .catch(() => {})
                .finally(() => setListingLoading(false));
        }
    };

    useEffect(() => {
        updateAudioStatus();

        const interval = setInterval(() => {
            if (!isEdit) updateAudioStatus();
        }, 15000);

        return () => {
            clearInterval(interval);
        };
    }, [isEdit]);

    useEffect(() => {
        progressIdRef.current = inProgressNid;
    }, [inProgressNid]);

    useEffect(() => {
        let isDifferent = audiosRef.current !== audiosInPlaylist;
        audiosRef.current = audiosInPlaylist;
        if (isDifferent || isInitialRun.current) updateAudioStatus();
    }, [audiosInPlaylist]);

    return {
        audioList,
        setAudioList,
        listingLoading,
        updateAudioStatus,
    };
};

const useHandlers = () => {
    const { loading: removeLoading, removeAudio } = useRemoveAudio();

    const onConfirmRemoveAudioFromPlaylist = async (
        id: string,
        audio: Content | ReceivedGift,
        isGift: boolean,
    ) => removeAudio(id, audio, isGift);

    return {
        onConfirmRemoveAudioFromPlaylist,
        removeLoading,
    };
};

export interface PlaylistDetailProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export interface PlaylistSortable {
    value: Content | ReceivedGift;
    isEdit: boolean;
    handleAudioRemoval: () => void;
    isDownloadInProgress?: boolean;
    isInDownloadQueue?: boolean;
    isDownloaded?: Promise<boolean>;
    inProgressDownloaded?: boolean;
    isResume: boolean;
    isFavouriteContent: boolean;
    imageUrl: string;
    handleListItemClick: () => void;
    itemRef: (el) => void;
}

const SortableAudio = (props: PlaylistSortable & { id: string }) => {
    const {
        value,
        isEdit,
        isDownloadInProgress,
        isInDownloadQueue,
        isDownloaded,
        inProgressDownloaded,
        isResume,
        imageUrl,
        isFavouriteContent,
        handleListItemClick,
        handleAudioRemoval,
        itemRef,
        id,
    } = props;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style}>
            <PlaylistElement
                itemRef={itemRef}
                value={value}
                isEdit={isEdit}
                isDownloadInProgress={isDownloadInProgress}
                isInDownloadQueue={isInDownloadQueue}
                isDownloaded={isDownloaded}
                inProgressDownloaded={inProgressDownloaded}
                isResume={isResume}
                isFavouriteContent={isFavouriteContent}
                imageUrl={imageUrl}
                handleListItemClick={handleListItemClick}
                handleAudioRemoval={handleAudioRemoval}
                dragHandleProps={{ ...attributes, ...listeners }}
            />
        </Box>
    );
};

export interface SortableAudiosProps {
    items: PlaylistAudio[];
    isFavouriteContent: (filteredAudio: ReceivedGift | Content) => boolean;
    isEdit: boolean;
    isResumeable: (value: Content | ReceivedGift) => boolean;
    thumbnails: {
        [key: string]: string;
    };
    id: string;
    setAudioForRemoval: React.Dispatch<
        React.SetStateAction<ReceivedGift | Content>
    >;
    itemRefs: React.MutableRefObject<any[]>;
    onSortEnd: (event: DragEndEvent) => void;
}

const SortableAudios = (props: SortableAudiosProps) => {
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { addScrollIndexInListingUrl } = useGestureToScroll();

    const {
        items,
        isFavouriteContent,
        isEdit,
        isResumeable,
        thumbnails,
        id,
        setAudioForRemoval,
        itemRefs,
        onSortEnd,
    } = props;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onSortEnd}
            modifiers={[restrictToVerticalAxis]}
        >
            <SortableContext
                items={items?.map((a) => a.audio.nid) || []}
                strategy={verticalListSortingStrategy}
            >
                <Box>
                    {items?.map((a, i) => (
                        <React.Fragment key={a.audio.nid}>
                            <SortableAudio
                                id={a.audio.nid}
                                itemRef={(el) => (itemRefs.current[i] = el)}
                                isEdit={isEdit}
                                isDownloadInProgress={
                                    a.state === AudioDownloadState.DOWNLOADING
                                }
                                isInDownloadQueue={
                                    a.state === AudioDownloadState.QUEUED
                                }
                                isDownloaded={
                                    new Promise<boolean>((res, _) => {
                                        res(a.state === AudioDownloadState.DOWNLOADED);
                                    })
                                }
                                isResume={isResumeable(a.audio)}
                                isFavouriteContent={isFavouriteContent(a.audio)}
                                imageUrl={thumbnails && thumbnails[a.audio.nid]}
                                handleListItemClick={() => {
                                    if (!isEdit) {
                                        addScrollIndexInListingUrl(i - 2);
                                        navigate(
                                            linkProvider.react
                                                .playlist()
                                                .play(
                                                    id,
                                                    items?.indexOf(a).toString(),
                                                    "0",
                                                    { scrollTo: i },
                                                ),
                                        );
                                    }
                                }}
                                handleAudioRemoval={() => setAudioForRemoval(a.audio)}
                                value={a.audio}
                            />
                            <AppDivider />
                        </React.Fragment>
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
};

const useQueryParams = () => {
    const location = useLocation();
    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo,
        };
    }, [location?.search]);
};

const FabWrapper = styled(Box)(({ theme }) => ({
    top: `calc(${theme.header.height})`,
    display: "flex",
    justifyContent: "start",
    columnGap: "5px",
    paddingLeft: "16px",
    flexDirection: "row",
    color: theme.colors.white,
}));

const StyledFab = styled(Fab)(({ theme }) => ({
    background:
        theme.palette.mode === "dark"
            ? theme.palette.primary.main
            : theme.palette.grey[600],
    color: theme.colors.white,
    "&:hover": {
        background:
            theme.palette.mode === "dark"
                ? theme.palette.primary.main
                : theme.palette.grey[600],
        color: theme.colors.white,
    },
}));

const StyledLoader = styled(Box)(({ theme }) => ({
    color: theme.palette.common.white,
}));

const WhiteCircularProgress = styled(AppCircularProgress)(({ theme }) => ({
    color: theme.palette.common.white,
}));

const ContentTypography = styled(AppTypography)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
});

const HeaderWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
    width: "100%",
    height: "17vh",
    paddingBottom: "10px",
    zIndex: 100,
}));

const FixedListing = styled(Box)({
    overflowY: "scroll",
    position: "relative",
    left: "0",
    width: "100%",
    height: "72vh",
});

const ContentWrapper = styled(Box)(({ theme }) => ({
    height: "100%",
    paddingBottom: "60px",
    overflowX: "hidden",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
}));
